/**
 * Files behind authentication, fetched with the Authorization header.
 *
 * Tokens never go in URLs (Rule 13 §4): a URL is logged, kept in history and
 * can be shared. A plain link or window.open cannot send a header, so the file
 * is fetched with apiClient and handed to the browser from memory instead.
 * The report, export and briefing endpoints refuse a token in the URL.
 */

import { apiClient } from '@/lib/api-client'

// Long enough for the new tab to load the PDF before its object URL is revoked.
const OPENED_URL_LIFETIME_MS = 60_000

/** Download a file under the given name. Throws if the request fails. */
export async function saveAuthedFile(endpoint: string, fileName: string): Promise<void> {
  const blob = await apiClient.getBlob(endpoint)
  const href = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = href
  a.download = fileName
  a.style.display = 'none'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(href)
}

/**
 * Open a file (a PDF) in a new tab. Call it straight from the click handler,
 * before any await: the tab is opened first so the popup blocker allows it,
 * then pointed at the file once fetched. If the browser blocks the tab, the
 * file is downloaded instead. Throws if the request fails.
 */
export async function openAuthedFile(endpoint: string, fileName: string): Promise<void> {
  const tab = window.open('', '_blank')
  if (tab) tab.opener = null
  try {
    const blob = await apiClient.getBlob(endpoint)
    const href = URL.createObjectURL(blob)
    if (tab) {
      tab.location.href = href
      window.setTimeout(() => URL.revokeObjectURL(href), OPENED_URL_LIFETIME_MS)
      return
    }
    const a = document.createElement('a')
    a.href = href
    a.download = fileName
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(href)
  } catch (err) {
    tab?.close()
    throw err
  }
}
